/**
 * nxModal — 공통 팝업. 다른 페이지를 AJAX 로 불러 팝업 안에 띄우고,
 *           [저장] 을 눌렀을 때만 부모에게 결과를 돌려준다.
 *
 * 사용 (부모)
 * ----------
 *   const r = await nx.modal.open('/docs/123/edit', {
 *     size: 'sheet',                  // sheet(기본) | inset | full
 *     title: '문서 수정',
 *     context: { rowId: 123 },        // 팝업 안에서 nx.modal.context 로 읽는다
 *   });
 *   if (r) updateRow(r);              // [저장] 일 때만 값이 온다. 나머지는 null
 *
 * 사용 (팝업 안에서 부모로 돌려주기) — 셋 중 아무거나
 *   1) 버튼에 값 박기:  <button data-nx-modal-save='{"agree":true}'>동의</button>
 *   2) 폼 제출:        <form method="post" action="...">  → 서버가 JSON 을 주면 그게 결과
 *   3) 코드에서:        nx.modal.done({ id: 1, title: '...' })
 *
 * 닫히는 4경로 — 뒤로가기 · [닫기](data-nx-modal-close) · [저장] · 우측 상단 ×
 *   **[저장]만** 결과를 전달한다. 나머지는 모두 null 이다.
 *
 * 서버 쪽
 * ------
 *   요청에 `X-NX-Modal: 1` 이 붙는다. 서버는 이 헤더를 보면 셸(헤더·사이드바)을
 *   빼고 본문 조각만 돌려준다(core/nx/templating.py 의 nx_layout).
 *   그래서 **같은 URL 을 일반 페이지로도, 팝업으로도** 쓸 수 있다.
 */

/* D001 — 팝업 마크업이 없는 화면의 «마지막 수단»은 이제 시스템 창이 아니라
   공용 대화상자다(부품 한 벌 — 0-0). */
import { dlgAlert, dlgConfirm, dlgPrompt, type DlgWords } from './dlg';

const HEADER = 'X-NX-Modal';
const MAX_DEPTH = 2;

export interface ModalOptions {
  /** sheet = Vertical Centered Scrollable(기본) · inset = 30px 여백 · full = 꽉 채움 */
  size?: 'sheet' | 'inset' | 'full';
  title?: string;
  /** 부모 → 팝업으로 넘기는 값. 팝업 안에서 nx.modal.context 로 읽는다. */
  context?: Record<string, unknown>;
  /** [저장] 결과 콜백. await 로 받는 것과 동시에 쓸 수 있다. */
  onSave?: (data: unknown) => void;
  /** 뒤로가기로 닫기(기본 true). 모바일에서 끄면 뒤로가기가 페이지를 떠난다. */
  history?: boolean;
}

interface Frame {
  overlay: HTMLElement;
  backdrop: HTMLElement;
  resolve: (v: unknown) => void;
  opts: ModalOptions;
  prevFocus: Element | null;
  settled: boolean;
  histPushed: boolean;
  /** F304: 지금 그려져 있는 조각의 URL — 위임 제출이 action 폴백으로 쓴다. */
  url?: string;
}

const stack: Frame[] = [];

/** 팝업 안의 코드가 읽는 값. 부모가 opts.context 로 넘긴 것. */
export let context: Record<string, unknown> = {};

// ---------------------------------------------------------------- helpers

function tpl(id: string): HTMLTemplateElement | null {
  return document.getElementById(id) as HTMLTemplateElement | null;
}

/** 크기별 클래스 문자열을 템플릿에서 읽는다(Tailwind purge 대비 — modal.ts 안에 두지 않는다). */
function classesFor(templateId: string, size: string): string | null {
  const t = tpl(templateId);
  const el = t?.content.querySelector<HTMLElement>(`[data-size="${size}"]`);
  return el ? el.getAttribute('class') : null;
}

/**
 * 서버가 준 오류 본문을 사람이 읽을 한 줄로.
 *
 * 모양이 셋이다:
 *   {ok:false, error:"…"}                       우리 API
 *   {detail:"tree.error.name_required"}         우리 HTTPException (i18n 키)
 *   {detail:[{loc,msg,type}, …]}                FastAPI 검증 실패(422) — **배열**
 *
 * 배열을 그냥 `String()` 하면 `[object Object]` 가 찍힌다(실제로 그랬다).
 */
/** 화면 문구 — 사전이 없으면 두 번째 인자를 쓴다(`app.ts` 의 nx.t 와 같은 규칙). */
function msg(key: string, fallback: string): string {
  const t = window.nx?.t;
  if (!t) return fallback;
  const s = t(key);
  return s === key ? fallback : s; // 키가 사전에 없으면 키를 그대로 돌려준다
}

/** D001 — 공용 대화상자에 줄 단추 글자. 사전은 이 파일이 이미 들고 있다. */
function dlgWords(): DlgWords {
  return { ok: msg('action.confirm', 'OK'), cancel: msg('action.cancel', 'Cancel') };
}

function errorText(data: { error?: unknown; detail?: unknown }): string {
  const raw = data.error ?? data.detail;
  if (Array.isArray(raw)) {
    const msgs = raw
      .map((d) => (d && typeof d === 'object' ? (d as { msg?: unknown }).msg : d))
      .filter(Boolean)
      .map(String);
    if (msgs.length) return msgs.join(' / ');
  }
  if (typeof raw === 'string') {
    // i18n 키(`tree.error.…`)면 사람이 읽을 문구로 바꾼다. 아니면 그대로.
    const t = window.nx?.t;
    return t ? t(raw) : raw;
  }
  return msg('modal.save_failed', 'Could not save.');
}

/** 가져온 조각의 <script> 는 innerHTML 로 넣으면 실행되지 않는다 → 다시 만들어 붙인다. */
function runScripts(root: HTMLElement): void {
  root.querySelectorAll('script').forEach((old) => {
    const s = document.createElement('script');
    for (const a of Array.from(old.attributes)) s.setAttribute(a.name, a.value);
    s.textContent = old.textContent;
    old.replaceWith(s);
  });
}

/**
 * 뒤 화면 스크롤 잠금.
 *
 * ★ 참조 카운트로 센다. 팝업은 2단까지 겹칠 수 있는데, 켜고/끄기로만 두면
 *   **안쪽 팝업이 닫힐 때 잠금이 풀려** 바깥 팝업이 떠 있는 채로 뒤가 스크롤된다.
 *
 * ★ 폭 보정 — `overflow:hidden` 이 되면 스크롤바가 사라져 본문이 15~17px
 *   오른쪽으로 밀린다(화면이 흔들린다). 평소에는 `nx-form.css` 의
 *   `scrollbar-gutter: stable` 이 자리를 잡아 두므로 아무것도 안 해도 된다.
 *   그 속성을 모르는 브라우저에서만 `padding-right` 로 대신 메운다.
 *   **잠그기 전에** 재야 한다 — 잠근 뒤에는 이미 스크롤바가 없어 0이 나온다.
 */
let lockDepth = 0;
const GUTTER_OK =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('scrollbar-gutter', 'stable');

function lockScroll(on: boolean): void {
  const before = lockDepth;
  lockDepth = Math.max(0, lockDepth + (on ? 1 : -1));
  const locked = lockDepth > 0;
  if (locked === before > 0) return; // 상태가 안 바뀌면 손대지 않는다

  if (!GUTTER_OK && locked) {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
  }
  document.body.classList.toggle('nx-modal-open', locked);
  if (!locked) document.body.style.paddingRight = '';
}

/** Tab 이 팝업 밖으로 나가지 않게 한다. */
function trapFocus(overlay: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const sel =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const items = Array.from(overlay.querySelectorAll<HTMLElement>(sel)).filter(
    (el) => el.offsetParent !== null,
  );
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/**
 * 우리가 부른 `history.back()` 을 세어 그만큼의 popstate 를 흘려보낸다.
 *
 * 왜 — `settle()` 이 `history.back()` 을 부르면 popstate 가 온다. 그걸 그냥 두면
 * **스택의 새 꼭대기(= 바깥 팝업)** 를 닫아 버린다(중첩에서 안쪽만 닫았는데
 * 바깥까지 닫힌다).
 *
 * ★ 다만 **셈이 어긋날 수 있다.** 한 틱 안에 back() 이 두 번 나가면 브라우저가
 *   popstate 를 두 번 안 줄 수도 있다(사파리). 그러면 카운터가 1로 남아
 *   **다음에 진짜 누른 뒤로가기가 삼켜진다.** 그래서 시각을 함께 적어 두고
 *   1초가 지난 표는 무효로 본다 — 스스로 회복한다.
 */
let selfBack = 0;
let selfBackAt = 0;

function markSelfBack(): void {
  selfBack += 1;
  selfBackAt = Date.now();
}

/** 이번 popstate 가 우리가 부른 back 때문인가. 맞으면 표를 한 장 쓴다. */
function consumeSelfBack(): boolean {
  if (selfBack <= 0) return false;
  if (Date.now() - selfBackAt > 1000) {
    selfBack = 0; // 오래된 표는 버린다(브라우저가 popstate 를 안 준 경우)
    return false;
  }
  selfBack -= 1;
  return true;
}

/**
 * F163 — **nx.modal 밖의 오버레이**에 「뒤로가기 한 칸」을 붙인다.
 *
 * 왜 여기 있는가: 뒤로가기 처리는 `settle()` 의 **selfBack 카운터**와 같은
 * 셈을 써야 한다. 오버레이가 제 손으로 `history.back()` 을 부르면 그 popstate
 * 를 modal.ts 의 리스너가 받아 **밑에 깔린 팝업까지 닫아 버린다.** 같은
 * 카운터를 쓰면 그 back 은 표를 내고 조용히 지나간다.
 *
 * 쓰는 법:
 *   const back = nx.modal.backCloser(() => close(true));   // 열 때
 *   back.release();        // 버튼·Esc 로 닫을 때  (기록 한 칸을 되돌린다)
 *   back.release(true);    // 뒤로가기로 닫혔을 때(이미 빠졌다 — 또 부르면
 *                          //   진짜 이전 화면으로 나간다, F51·F162 교훈)
 *
 * 한계(적어 둔다): nx.modal 팝업이 떠 있는 **위에** 이 오버레이를 또 열면,
 * 진짜 뒤로가기 한 번에 둘 다 닫힌다(리스너가 둘 다 산다). 지금 화면 중
 * 그렇게 겹치는 곳은 없다 — 겹치게 되면 그때 스택을 하나로 합친다.
 */
export interface BackCloser {
  release(fromBack?: boolean): void;
}

export function backCloser(onBack: () => void): BackCloser {
  let pushed = false;
  try {
    history.pushState({ nxBack: 1 }, '');
    pushed = true;
  } catch { /* 기록을 못 쓰는 환경 — 닫기 버튼은 그대로 된다 */ }

  const onPop = (): void => {
    if (consumeSelfBack()) return;      // 우리가 부른 back — 흘려보낸다
    window.removeEventListener('popstate', onPop);
    pushed = false;
    onBack();
  };
  window.addEventListener('popstate', onPop);

  return {
    release(fromBack = false): void {
      window.removeEventListener('popstate', onPop);
      if (!fromBack && pushed) {
        try {
          markSelfBack();
          history.back();
        } catch {
          selfBack = Math.max(0, selfBack - 1);
        }
      }
      pushed = false;
    },
  };
}

// ---------------------------------------------------------------- open / close

/**
 * 내용 없이 **틀만** 만든다. `open()` 은 여기에 fetch 를 얹고,
 * `alert()` 는 만들어 둔 HTML 을 얹는다.
 *
 * ★ 왜 나눴나 — 화면 안에서 난 오류(서버 왕복이 아닌 것)를 알릴 곳이 없어서
 *   `window.alert()` 를 쓰고 있었다. 브라우저 기본 경고창은 우리 화면과
 *   생김새가 완전히 달라서, 같은 "로그인이 필요합니다" 가 어떤 길로 왔느냐에
 *   따라 **팝업으로도 뜨고 경고창으로도 떴다**(실제로 그랬다).
 */
function begin(
  opts: ModalOptions,
): { frame: Frame; promise: Promise<unknown> } | null {
  if (stack.length >= MAX_DEPTH) {
    // 3단 중첩은 사용자가 길을 잃는다. 조용히 삼키지 않고 알린다.
    console.warn(`[nxModal] 중첩 한도(${MAX_DEPTH}) 초과 — 열지 않습니다.`);
    return null;
  }
  const t = tpl('nx-modal-tpl');
  if (!t) {
    // 셸이 partials/_modal_root.html 을 include 하지 않았다.
    console.warn('[nxModal] #nx-modal-tpl 없음');
    return null;
  }

  const depth = stack.length + 1;
  const frag = t.content.cloneNode(true) as DocumentFragment;
  const overlay = frag.querySelector<HTMLElement>('[data-nx-modal]')!;
  const box = overlay.querySelector<HTMLElement>('[data-nx-modal-box]')!;
  const card = overlay.querySelector<HTMLElement>('[data-nx-modal-card]')!;
  const size = opts.size ?? 'sheet';

  const boxCls = classesFor('nx-modal-sizes', size);
  if (boxCls) box.setAttribute('class', boxCls);
  const cardCls = classesFor('nx-modal-cards', size);
  if (cardCls) card.setAttribute('class', cardCls);
  overlay.dataset.nxDepth = String(depth);

  const backdrop = document.createElement('div');
  backdrop.className = 'nx-modal-backdrop';
  backdrop.dataset.nxDepth = String(depth);

  document.body.append(backdrop, overlay);

  let resolve!: (v: unknown) => void;
  const promise = new Promise<unknown>((r) => (resolve = r));
  const frame: Frame = {
    overlay,
    backdrop,
    resolve,
    opts,
    prevFocus: document.activeElement,
    settled: false,
    histPushed: false,
  };
  stack.push(frame);
  context = opts.context ?? {};

  const titleEl = overlay.querySelector<HTMLElement>('[data-nx-modal-title]')!;
  if (opts.title) titleEl.textContent = opts.title;

  // --- 열기(트랜지션이 돌게 한 프레임 뒤에 상태 클래스를 붙인다) ---
  overlay.classList.remove('hidden');
  lockScroll(true);
  requestAnimationFrame(() => {
    overlay.classList.add('open', 'nx-open');
    backdrop.classList.add('nx-open');
  });

  // ★ **지금 바로** 초점을 팝업으로 옮긴다.
  //   예전에는 `inject()` 안에서만 옮겼다. 그런데 내용은 fetch 가 끝나야 온다 —
  //   그 사이(느린 회선에서는 몇 초다) 초점이 **뒤 페이지에 남아 있어**
  //   Tab 을 누르면 팝업이 아니라 뒤쪽 링크들을 돌아다녔고, 스크린리커는
  //   팝업이 열린 줄도 몰랐다. 불러오기가 **실패한** 경로에서는 끝내 옮겨지지
  //   않았다. 템플릿의 오버레이에 `tabindex="-1"` 이 있어 지금 받을 수 있다.
  overlay.focus?.();

  // --- 제출 가로채기(위임 — F304) ---
  // submit 은 버블된다. 조각 안 스크립트가 내용을 통째로 갈아 끼워도
  // (다운로드 팝업 폴링의 replaceWith) 오버레이의 이 리스너 하나가 받는다.
  // 폼별 리스너로는 갈아 끼운 새 폼이 **맨몸**이라 전체 화면 이동이 됐다.
  overlay.addEventListener('submit', (e) => {
    if (e.defaultPrevented) return;            // 조각이 스스로 처리한 폼
    const formEl = e.target as HTMLFormElement | null;
    if (!formEl || !formEl.matches('form') || formEl.matches('[data-nx-modal-plain]')) return;
    e.preventDefault();
    void submit(frame, formEl, frame.url || location.pathname);
  });

  // --- 닫기 경로들 ---
  backdrop.addEventListener('click', () => askClose(frame));
  overlay.addEventListener('click', (e) => {
    const el = e.target as HTMLElement;
    if (el.closest('[data-nx-modal-close]')) {
      e.preventDefault();
      askClose(frame);
      return;
    }
    const save = el.closest<HTMLElement>('[data-nx-modal-save]');
    if (save) {
      e.preventDefault();
      const raw = save.dataset.nxModalSave || '';
      let data: unknown = true;
      if (raw.trim()) {
        try {
          data = JSON.parse(raw);
        } catch {
          data = raw;
        }
      }
      settle(frame, data);
    }
  });
  const onKey = (e: KeyboardEvent): void => {
    if (stack[stack.length - 1] !== frame) return;
    if (e.key === 'Escape') {
      // ★ F931(검수 2026-10-02 · 사장님 «고친다») — 위에 뜬 확인창(`dlg.ts` — 캡처 단계에서 받아
      //   `preventDefault` 한다)이 이미 처리한 Esc 다. 여기서 또 닫기를 물으면 확인창이 닫히자마자
      //   **다시 뜬다**(키보드로는 빠져나올 수 없었다 — 크로미움에서 돌려서 확인).
      if (e.defaultPrevented) return;
      e.preventDefault();
      askClose(frame);
      return;
    }
    trapFocus(overlay, e);
  };
  document.addEventListener('keydown', onKey);
  (frame as Frame & { _onKey?: typeof onKey })._onKey = onKey;

  // --- 뒤로가기로 닫기 ---
  if (opts.history !== false) {
    try {
      history.pushState({ nxModal: depth }, '', location.href);
      frame.histPushed = true;
    } catch {
      /* 일부 브라우저/샌드박스에서 막혀도 팝업 자체는 동작해야 한다 */
    }
  }

  return { frame, promise };
}

export function open(url: string, opts: ModalOptions = {}): Promise<unknown> {
  // F320(사장님 간헐 재현 "팝업도 되고 전체 화면도 된다"): **같은 URL 연타
  // 가드** — 더블클릭이 팝업을 두 겹으로 열면 다음 열기부터 중첩 한도(2)에
  // 걸려 전체 화면 폴백으로 빠진다. 꼭대기가 같은 URL 이면 그 판을 돌려준다.
  const top = stack[stack.length - 1];
  if (top && top.url === url && !top.settled) {
    return (top as Frame & { _promise?: Promise<unknown> })._promise
      ?? Promise.resolve(null);
  }
  const started = begin(opts);
  if (!started) {
    // 틀을 못 만들면(셸에 팝업 마크업이 없다) 일반 페이지로 이동한다 —
    // 기능이 죽는 것보다 낫다. ★ F320: 왜 전체 화면이 됐는지 흔적을 남긴다.
    console.warn('[nxModal] 전체 화면 폴백:', url,
      stack.length >= MAX_DEPTH ? '중첩 한도' : '팝업 틀 없음');
    if (stack.length < MAX_DEPTH) window.location.href = url;
    return Promise.resolve(null);
  }
  started.frame.url = url;               // 연타 가드가 대조할 URL
  (started.frame as Frame & { _promise?: Promise<unknown> })._promise = started.promise;
  void load(started.frame, url);
  return started.promise;
}

/**
 * 알림 팝업 — **브라우저 경고창(window.alert) 대신** 쓴다.
 *
 * 화면 안에서 난 오류(서버가 400 을 준 뒤 그 문구를 보여줄 자리가 없는 경우 등)를
 * 나머지 팝업과 **같은 모양**으로 알린다. 확인 버튼 하나만 있다.
 *
 * ★ 문구는 반드시 `textContent` 로 넣는다. 서버가 준 글자를 HTML 로 넣으면
 *   그대로 화면에 실행된다.
 */
export function alert(message: string, opts: ModalOptions = {}): Promise<unknown> {
  const started = begin({ size: 'sheet', ...opts });
  if (!started) {
    /* ★★★★★ D001(사장님 지시 2026-08-20: *"alert 안 나타나게 해줘. 전체공통"*)
       — 팝업 마크업이 없는 화면에서도 **시스템 창을 띄우지 않는다.** 공용
       대화상자 한 벌(`dlg.ts` + nx-shell-parts.css 의 `.nx-dlg`)로 간다. */
    return dlgAlert(message, dlgWords()).then(() => null);
  }
  const holder = document.createElement('div');
  const p = document.createElement('p');
  p.className = 'py-4 text-center';
  p.textContent = message;
  holder.append(p);

  const foot = document.createElement('template');
  foot.setAttribute('data-nx-modal-footer', '');
  const ok = document.createElement('button');
  ok.type = 'button';
  ok.className = 'ti-btn ti-btn-primary btn-wave !mb-0';
  ok.setAttribute('data-nx-modal-close', '');
  ok.textContent = msg('action.confirm', 'OK');
  foot.content.append(ok);
  holder.append(foot);

  inject(started.frame, holder.innerHTML, '');
  return started.promise;
}

/**
 * 확인 팝업 — **브라우저 확인창(window.confirm) 대신** 쓴다. F504.
 *
 * 사장님 지시(2026-08-17): *"삭제 컨펌을 브라우저 confirm 말고 우리 팝업으로."*
 * `alert()` 과 **같은 조각**을 쓰고 단추만 둘이다 — 두 벌을 만들지 않는다.
 *
 * 돌려주는 값: 지운다고 하면 `true`, [닫기]·Esc·뒤로가기면 `null`.
 * ★ «닫혔다» 와 «아니오» 를 가르지 않는다 — 둘 다 «하지 않는다» 이고,
 *   가르는 순간 부르는 쪽마다 다르게 해석해 조용히 갈린다.
 */
export function confirm(message: string,
                        opts: ModalOptions & { okLabel?: string } = {},
                       ): Promise<unknown> {
  const started = begin({ size: 'sheet', ...opts });
  if (!started) {
    //: D001 — 시스템 확인창 대신 공용 대화상자(dlg.ts). 값은 그대로 true|null.
    return dlgConfirm(message, dlgWords());
  }
  const holder = document.createElement('div');
  const p = document.createElement('p');
  p.className = 'py-4 text-center';
  p.textContent = message;
  holder.append(p);

  const foot = document.createElement('template');
  foot.setAttribute('data-nx-modal-footer', '');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'ti-btn ti-btn-secondary btn-wave !mb-0';
  cancel.setAttribute('data-nx-modal-close', '');
  cancel.textContent = msg('action.close', 'Close');
  const ok = document.createElement('button');
  ok.type = 'button';
  ok.className = 'ti-btn ti-btn-danger btn-wave !mb-0';
  ok.setAttribute('data-nx-modal-ok', '');
  ok.textContent = opts.okLabel || msg('action.delete', 'Delete');
  foot.content.append(cancel, ok);
  holder.append(foot);

  inject(started.frame, holder.innerHTML, '');
  started.frame.overlay
    .querySelector<HTMLElement>('[data-nx-modal-ok]')
    ?.addEventListener('click', () => done(true));
  return started.promise;
}

/**
 * 이름 입력 팝업 — **브라우저 입력창(window.prompt) 대신** 쓴다. F154.
 *
 * 왜 필요한가(사장님 실측 2026-08-04): 목차 톱니의 [제목 수정]만 브라우저
 * prompt 로 떠서, 문서 이름 수정 팝업(dox_form)과 **모양이 달랐다.**
 * 여기서 만드는 조각은 그 폼과 같은 마크업(라벨 `ti-form-label` + 입력칸
 * `form-control` + 바닥 [닫기]/[저장])이라 **같은 팝업으로 보인다.**
 *
 * ★ 값은 `setAttribute('value', …)` 로 넣는다 — `input.value = …` 는
 *   직렬화(innerHTML)에 안 실려 빈 칸으로 뜬다.
 */
export function prompt(label: string, value: string,
                       opts: ModalOptions = {}): Promise<unknown> {
  const started = begin({ size: 'sheet', ...opts });
  if (!started) {
    return dlgPrompt(label, value, dlgWords());           // D001 — 시스템 창 금지
  }
  const holder = document.createElement('div');
  const wrap = document.createElement('div');
  wrap.className = 'mb-3';
  const lab = document.createElement('label');
  lab.className = 'ti-form-label';
  lab.setAttribute('for', 'nx-modal-prompt');
  lab.textContent = label;
  const inp = document.createElement('input');
  inp.type = 'text';
  inp.id = 'nx-modal-prompt';
  inp.className = 'form-control';
  inp.setAttribute('value', value || '');
  inp.setAttribute('autofocus', '');
  wrap.append(lab, inp);
  holder.append(wrap);

  const foot = document.createElement('template');
  foot.setAttribute('data-nx-modal-footer', '');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'ti-btn ti-btn-secondary btn-wave !mb-0';
  cancel.setAttribute('data-nx-modal-close', '');
  cancel.textContent = msg('action.close', 'Close');
  const save = document.createElement('button');
  save.type = 'button';
  save.className = 'ti-btn ti-btn-primary btn-wave !mb-0';
  save.setAttribute('data-nx-modal-save', '');
  save.textContent = msg('action.save', 'Save');
  foot.content.append(cancel, save);
  holder.append(foot);

  inject(started.frame, holder.innerHTML, '');

  const overlay = started.frame.overlay;
  const field = overlay.querySelector<HTMLInputElement>('#nx-modal-prompt');
  const ok = overlay.querySelector<HTMLElement>('[data-nx-modal-save]');
  const submit = () => done((field?.value ?? '').trim());
  ok?.addEventListener('click', submit);
  field?.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'Enter') { ev.preventDefault(); submit(); }
  });
  field?.focus();
  field?.select();
  return started.promise;
}

async function load(frame: Frame, url: string): Promise<void> {
  const body = frame.overlay.querySelector<HTMLElement>('[data-nx-modal-body]')!;
  try {
    const r = await fetch(url, {
      // ★ Accept 를 명시한다. `fetch()` 기본값은 `*/*` 라 서버가
      //   "이 요청은 HTML 을 원하지 않는다" 로 읽어 JSON 오류를 그대로 준다.
      headers: { [HEADER]: '1', 'X-Requested-With': 'nx-modal', Accept: 'text/html' },
      // ★★ 2026-08-15(사장님 실기동 — «팝업 안에 MY DOX 가 통째로»):
      //   같은 URL 이 헤더에 따라 **조각과 통짜 페이지** 두 가지를 낸다.
      //   캐시에 통짜 쪽이 들어 있으면 브라우저는 그것을 팝업 요청에도 준다.
      //   서버에 `Vary` 를 붙였지만 **이미 옛 규칙으로 캐시된 것**은 그 헤더를
      //   모른다 — 여기서 캐시를 아예 안 쓴다. 조각은 매번 새로 받는다.
      cache: 'no-store',
      credentials: 'same-origin',
    });
    const text = await r.text();
    // F320(사장님 간헐 재현): 전에는 **리다이렉트면 무조건** 전체 화면으로
    // 이동했다 — 서버가 한 번이라도 넘기면(언어 경로 정규화 등) 팝업 대신
    // /download 전체 화면이 됐다("팝업도 되고 전체 화면도 된다"의 정체 후보).
    // fetch 는 같은 헤더로 따라갔으므로 최종 응답이 **조각**이면(문서 선언이
    // 없다) 그대로 팝업에 그린다. 진짜 다른 화면(로그인 등 전체 문서)만
    // 이동하고, 이동할 때는 콘솔에 이유를 남긴다.
    if (r.redirected && r.url) {
      if (r.ok && !/^\s*<!doctype/i.test(text)) {
        console.warn('[nxModal] 리다이렉트 응답이 조각이라 팝업에 그린다:', r.url);
      } else {
        console.warn('[nxModal] 전체 화면 폴백(리다이렉트):', r.url);
        window.location.href = r.url;
        return;
      }
    }
    // ★ 401/403 에서 `r.url` 로 이동하면 안 된다 — 리다이렉트가 아니라
    //   **방금 요청한 그 주소**라, 브라우저가 그 URL 을 다시 열어
    //   `{"detail":"login required"}` JSON 만 화면에 찍힌다(실제로 그랬다).
    //   게다가 뒤로가기를 하면 이 팝업이 **빈 채로 되살아난다.**
    //   서버가 팝업 요청(X-NX-Modal)에는 안내 HTML 조각을 주므로 그대로 그린다.
    //
    // ★★★ 2026-08-15 사장님 실기동 — **통짜 문서는 팝업에 넣지 않는다.**
    //   옛 판은 받은 것을 그대로 `innerHTML` 에 넣었다. 그래서 서버나 캐시가
    //   `<!doctype html>` 통짜 화면을 주면 **팝업 안에 MY DOX 가 통째로**
    //   들어앉았다(머리띠·빵부스러기·[DOX 만들기]까지 두 번). 사용자가 볼
    //   수 있는 화면 중 가장 못 믿을 꼴이다.
    //   ★ 여기서 «왜 통짜가 왔는가» 를 따지지 않는다 — 원인이 무엇이든
    //     **그 주소를 그냥 전체 화면으로 연다.** 사용자는 원하던 화면을
    //     본다(팝업이 아닐 뿐이다). 기능이 사는 쪽으로 어긋난다.
    if (/^\s*(<!doctype|<html)/i.test(text)) {
      console.warn('[nxModal] 통짜 문서를 받았다 — 전체 화면으로 연다:', url);
      window.location.assign(url);
      return;
    }
    inject(frame, text, url);
  } catch {
    body.innerHTML =
      '<p class="py-8 text-center text-danger" data-nx-fragment>' +
      msg('modal.load_failed', 'Could not load. Please try again.') +
      '</p>';
  }
}

/** 받은 HTML 을 팝업에 넣고, 제목·푸터·폼을 배선한다. */
function inject(frame: Frame, html: string, url: string): void {
  const overlay = frame.overlay;
  const body = overlay.querySelector<HTMLElement>('[data-nx-modal-body]')!;
  const footer = overlay.querySelector<HTMLElement>('[data-nx-modal-footer]')!;
  const titleEl = overlay.querySelector<HTMLElement>('[data-nx-modal-title]')!;

  const holder = document.createElement('div');
  holder.dataset.nxFragment = '';
  holder.innerHTML = html;

  // 조각이 제목을 지정했으면 쓴다(부모의 opts.title 보다 우선).
  const t = holder.querySelector<HTMLElement>('[data-nx-modal-title-text]');
  if (t) {
    titleEl.textContent = t.textContent || '';
    t.remove();
  }

  // 조각이 <template data-nx-modal-footer> 로 준 버튼을 푸터로 옮긴다.
  const f = holder.querySelector<HTMLTemplateElement>('template[data-nx-modal-footer]');
  if (f) {
    footer.replaceChildren(f.content.cloneNode(true));
    f.remove();
  } else {
    footer.replaceChildren();
    footer.classList.add('hidden');
  }

  body.replaceChildren(holder);
  runScripts(holder);

  // 팝업 안의 폼 제출은 페이지를 떠나지 않는다 — 가로채기는 **위임**이라
  // (openFrame 의 오버레이 submit 리스너) 여기서는 조각 URL 만 적어 둔다.
  // ★ F304(사장님 실기동 2026-08-09): 전에는 여기서 **폼마다** 리스너를
  //   붙였다. 다운로드 팝업의 3초 폴링이 상태 영역을 replaceWith 로 갈아
  //   끼우면 새 폼에는 리스너가 없어 브라우저 기본 제출 = **전체 화면
  //   이동**이 됐다(팝업이 사라지고 /download/make 가 통째로 열림).
  frame.url = url;

  // ★ Preline(아코디언·드롭다운·탭…)은 DOM 로드 때 한 번만 스스로 붙는다.
  //   조각 안의 `hs-*` 컴포넌트는 그 뒤에 들어오므로 여기서 다시 붙여 준다.
  //   (없으면 조용히 넘어간다 — 셸이 preline 을 안 싣는 화면도 있다)
  try {
    window.HSStaticMethods?.autoInit?.();
  } catch {
    /* 무시 — 팝업 내용은 그대로 보인다 */
  }

  // ★ 조각이 들어왔음을 알린다. 부팅 때 한 번만 훑는 코드(트리 배선 등)는
  //   나중에 끼워진 DOM 을 못 보므로, 이 신호를 받아 자기 몫을 다시 붙인다.
  document.dispatchEvent(
    new CustomEvent('nx:modal:load', { detail: { root: holder, url } }),
  );

  const first = holder.querySelector<HTMLElement>(
    'input:not([type=hidden]),select,textarea,button',
  );
  (first ?? overlay).focus?.();
}

async function submit(frame: Frame, form: HTMLFormElement, baseUrl: string): Promise<void> {
  const action = form.getAttribute('action') || baseUrl;
  const method = (form.getAttribute('method') || 'post').toUpperCase();
  const fd = new FormData(form);
  // ★ 폼 **바깥**의 제출 버튼도 모은다.
  //   팝업 바닥 버튼은 `<template data-nx-modal-footer>` 안에 있다가 푸터로
  //   옮겨지므로 폼 밖이다. `form.querySelectorAll` 만 보면 그 버튼이 비활성화
  //   되지 않아, 느린 회선에서 두 번 누르면 **같은 것이 두 건 만들어진다.**
  const btns = Array.from(
    form.querySelectorAll<HTMLButtonElement>('button[type=submit]'),
  );
  if (form.id) {
    document
      .querySelectorAll<HTMLButtonElement>(
        `button[type=submit][form="${CSS.escape(form.id)}"]`,
      )
      .forEach((b) => {
        if (!btns.includes(b)) btns.push(b);
      });
  }
  // F295(사장님 2026-08-09): 누른 뒤 재렌더까지 아무 표시가 없어 또 누르게
  // 된다 — 비활성화에 **스피너**를 더한다. 아이콘 폰트·CSS 애니메이션에
  // 기대지 않도록 SMIL 회전 SVG 를 쓴다(경량 셸·뷰어 팝업에서도 돈다).
  btns.forEach((b) => {
    b.disabled = true;
    b.setAttribute('aria-busy', 'true');
    if (!b.querySelector('[data-nx-spin]')) {
      const s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      s.setAttribute('data-nx-spin', '');
      s.setAttribute('width', '13');
      s.setAttribute('height', '13');
      s.setAttribute('viewBox', '0 0 24 24');
      s.setAttribute('fill', 'none');
      s.setAttribute('aria-hidden', 'true');
      s.style.verticalAlign = '-2px';
      s.style.marginRight = '6px';
      s.innerHTML =
        '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3"'
        + ' stroke-linecap="round" stroke-dasharray="42 18">'
        + '<animateTransform attributeName="transform" type="rotate"'
        + ' from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/>'
        + '</circle>';
      b.prepend(s);
    }
  });
  try {
    const r = await fetch(action, {
      method,
      body: method === 'GET' ? undefined : fd,
      // ★ CSRF — 팝업 안의 폼은 `FormData` 로 보내므로 본문이 multipart 다.
      //   서버는 multipart 본문을 뒤지지 않는다(업로드를 통째로 메모리에 담지
      //   않으려고). 그래서 **헤더로** 토큰을 보낸다(core/nx/csrf.py).
      //   폼 안에 숨은 필드도 함께 들어 있지만, 그건 JS 없이 제출될 때의 몫이다.
      headers: {
        [HEADER]: '1',
        'X-Requested-With': 'nx-modal',
        ...(window.nx?.csrfHeaders?.() ?? {}),
      },
      credentials: 'same-origin',
    });
    const ct = r.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const data = (await r.json()) as {
        ok?: boolean;
        error?: unknown;
        detail?: unknown;
        [k: string]: unknown;
      };
      // ★ **HTTP 상태를 먼저 본다.** 예전에는 `ok:false` 만 봤는데,
      //   FastAPI 의 검증 실패(422)나 서버 오류(500)는 `{"detail": …}` 형태라
      //   `ok` 가 없다. 그래서 **실패를 성공으로 알고 팝업을 닫아** 버렸다.
      if (!r.ok || data.ok === false) {
        showError(frame, errorText(data));
        return;
      }
      // ★★★★★ F556(사장님 실기동 2026-08-17 — "가져오기를 누르면 팝업이 다
      //   사라진다"): `settle()` 은 성공 JSON 이면 **무조건 오버레이를 지운다.**
      //   그래서 «올린 뒤 이 팝업에서 계속 기다린다»(F550·F555)가 성립할 수
      //   없었다 — 응답이 오는 순간 팝업이 통째로 없어지고, 그 뒤에 던지는
      //   신호는 받을 화면이 없었다. 서버가 `keep_open` 을 주면 **닫지 않고**
      //   팝업 안 스크립트에 그대로 넘긴다(이벤트는 버블 → document 까지).
      //   ★ 형식이 아니라 **응답이** 정한다 — 팝업마다 다시 배선하지 않는다.
      if (data.keep_open === true) {
        const host = frame.overlay.querySelector<HTMLElement>('[data-nx-modal-body]')
          ?? frame.overlay;
        host.dispatchEvent(new CustomEvent('nx:modal-keep', {
          detail: data, bubbles: true,
        }));
        return;
      }
      settle(frame, data);
      return;
    }
    // 서버 오류로 온 HTML(500 페이지 등)을 팝업에 그리면 사용자가 더 헷갈린다.
    if (r.status >= 500) {
      showError(frame, msg('modal.save_error', 'Something went wrong while saving.'));
      return;
    }
    // HTML 이 오면 검증 오류를 다시 그린 것이다 → 팝업 안에서 교체한다.
    // ★★★ 2026-08-15 사장님 실기동 — **통짜 문서면 그리지 않는다.**
    //   팝업 안의 제출은 `fetch` 라 **리다이렉트를 스스로 따라간다.** 서버가
    //   303 으로 목록을 가리키면 fetch 가 그 통짜 화면을 받아 오고, 옛 판은
    //   그것을 그대로 본문에 넣었다 — 그게 «팝업 안의 MY DOX» 였다.
    //   서버 쪽은 이제 팝업 제출에 JSON 을 주지만(`_back_or_json`), 아직 안
    //   고쳐진 자리나 딴 화면이 있어도 여기서 한 번 더 막는다.
    const _t = await r.text();
    if (r.redirected || /^\s*(<!doctype|<html)/i.test(_t)) {
      console.warn('[nxModal] 제출 응답이 통짜 문서다 — 그 화면으로 간다:', r.url || action);
      window.location.assign(r.url || action);
      return;
    }
    inject(frame, _t, action);
  } catch {
    showError(frame, msg('modal.save_error', 'Something went wrong while saving.'));
  } finally {
    // F295: 성공이면 어차피 내용이 통째로 교체되지만, 오류로 같은 화면에
    // 남을 때는 스피너를 떼고 다시 누를 수 있게 되돌린다.
    btns.forEach((b) => {
      b.disabled = false;
      b.removeAttribute('aria-busy');
      b.querySelectorAll('[data-nx-spin]').forEach((s) => s.remove());
    });
  }
}

function showError(frame: Frame, msg: string): void {
  const body = frame.overlay.querySelector<HTMLElement>('[data-nx-modal-body]')!;
  // ★ 먼저 있던 오류 줄을 지운다. 예전에는 그냥 `prepend` 만 해서
  //   [저장] 을 세 번 누르면 같은 문구가 **세 줄 쌓였고**, 본문이 아래로
  //   밀려 정작 고쳐야 할 입력칸이 화면 밖으로 나갔다.
  body.querySelectorAll<HTMLElement>(':scope > [data-nx-modal-error]').forEach((el) =>
    el.remove(),
  );
  const p = document.createElement('p');
  p.className = 'mb-3 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger';
  p.setAttribute('role', 'alert');
  p.dataset.nxModalError = '';
  p.textContent = msg;
  body.prepend(p);
  // 오류가 났으니 초점을 되돌려 스크린리더가 읽게 한다(role=alert 만으로는
  // 화면을 보고 있는 사람이 위로 스크롤됐다는 것을 모른다).
  body.scrollTop = 0;
}

/** 팝업 안에서 결과를 확정하고 닫는다. */
export function done(data: unknown): void {
  const frame = stack[stack.length - 1];
  if (frame) settle(frame, data);
}

/**
 * ★★★★★★ F796(사장님 실기동 2026-09-08: ***"소스변경 팝업 > 닫기 > 마이
 *   크레딧 페이지로 간다. 팝업이 일어날 때 페이지가 있잖아?"***)
 *
 * **지금 떠 있는 팝업의 «내용만» 다른 주소로 바꾼다 — 히스토리는 안 만진다.**
 *
 * 왜 필요한가 — 실측(2026-09-08, 크로미움):
 *
 *   `close()` 는 `history.back()` 을 부르고, `back()` 은 **비동기**다.
 *   소스변경 팝업은 그 직후에 `open()` 을 불렀는데(F794), 그 `pushState` 가
 *   **아직 처리 안 된 뒤로가기에 삼켜졌다** — 잰 값:
 *
 *       목록          h=3
 *       소스변경 열기  h=4  state={nxModal:1}
 *       close+open    h=4  state=null      ← **칸이 안 늘었다**
 *       업로드 닫기    → /ko/my/credits     ← 목록보다 한 칸 더 뒤로 갔다
 *
 *   즉 업로드 팝업은 «내가 한 칸 밀었다»(`histPushed`)고 믿는데 실제로는
 *   안 밀렸고, 닫을 때 **남의 칸**을 먹었다. 그 앞 칸이 마이크레딧이었다.
 *
 * ⇒ 팝업을 닫았다 다시 여는 대신 **같은 팝업의 내용을 갈아 끼운다.** 그러면
 *   여는 자리도 닫는 자리도 하나씩이라 칸이 어긋날 수 없다.
 * ★ 이것은 «닫은 뒤 어디로» 규칙을 바꾸는 것이 아니다(F785 에서 그렇게 하다
 *   화면을 다 망가뜨렸다). **히스토리를 아예 안 건드리는 길**을 하나 여는 것이다.
 */
export function goto(url: string): boolean {
  const frame = stack[stack.length - 1];
  if (!frame) return false;
  void load(frame, url);
  return true;
}

/** 결과 없이 닫는다(= [닫기] 와 같다). */
export function close(): void {
  const frame = stack[stack.length - 1];
  if (frame) settle(frame, null);
}

/**
 * ★★★★★★ F930(사장님 2026-10-02 «업로드가 끝나지 않았는데 창을 닫을 경우 경고») — **닫기 전에 안쪽에 묻는다.**
 *
 * 사람이 닫는 네 길(뒤로가기 · [닫기]/× · Esc · 바깥 누름)만 이 문을 지난다. 안쪽 조각이
 * `nx:modal-beforeclose`(취소 가능)를 받아 `preventDefault()` 하면 **닫지 않는다** — 안쪽이 스스로
 * 물어본 뒤 닫기로 하면 `nx.modal.close()` 를 부른다(그 길은 묻지 않으니 다시 막히지 않는다).
 * ★ 듣는 조각이 없으면 예전과 똑같이 바로 닫힌다(다른 팝업은 달라지는 것이 없다).
 * ★ 뒤로가기로 왔는데 막혔으면 브라우저가 이미 한 칸 물렸다 — 그 칸을 다시 밀어 둔다
 *   (안 밀면 다음 닫기의 `history.back()` 이 **팝업 밖 화면의 칸**을 먹는다 — F796 의 함정).
 */
function askClose(frame: Frame, fromHistory = false): void {
  if (frame.settled) return;
  const ev = new CustomEvent('nx:modal-beforeclose', { cancelable: true, bubbles: true });
  if (frame.overlay.dispatchEvent(ev)) {
    settle(frame, null, fromHistory);
    return;
  }
  if (fromHistory && frame.histPushed) {
    try {
      history.pushState({ nxModal: Number(frame.backdrop.dataset.nxDepth || '1') }, '', location.href);
    } catch {
      frame.histPushed = false;   // 못 밀었으면 닫을 때 뒤로 가지 않는다(남의 칸을 먹지 않게)
    }
  }
}

function settle(frame: Frame, result: unknown, fromHistory = false): void {
  if (frame.settled) return;
  frame.settled = true;

  const i = stack.indexOf(frame);
  if (i >= 0) stack.splice(i, 1);

  const onKey = (frame as Frame & { _onKey?: (e: KeyboardEvent) => void })._onKey;
  if (onKey) document.removeEventListener('keydown', onKey);

  frame.overlay.classList.remove('open', 'nx-open');
  frame.backdrop.classList.remove('nx-open');
  // 참조 카운트라 **닫을 때마다** 한 번씩 푼다(스택 길이로 판단하지 않는다).
  // 열 때 2번 걸고 닫을 때 1번만 풀면 잠금이 영영 안 풀린다.
  lockScroll(false);

  // 뒤로가기가 아닌 경로로 닫혔으면 pushState 로 넣은 항목을 되돌린다.
  //
  // ★ 우리가 부른 `history.back()` 도 popstate 를 일으킨다. 그 popstate 를
  //   그냥 두면 **스택의 새 꼭대기(= 바깥 팝업)** 를 닫아 버린다 —
  //   중첩 팝업에서 안쪽만 닫았는데 바깥까지 같이 닫힌다.
  //   그래서 "내가 부른 back" 을 세어 두고 그만큼은 popstate 를 무시한다.
  if (frame.histPushed && !fromHistory) {
    try {
      markSelfBack();
      history.back();
    } catch {
      selfBack = Math.max(0, selfBack - 1);
    }
  }

  // ★★★★★ F556(사장님 2026-08-17: *"목록에 아예 안 보이면 DB 에 쓰레기로
  //   남을 것 아닌가?"*): 팝업이 **닫히는 순간**을 안쪽 스크립트에 알린다.
  //   업로드 팝업은 이 신호를 받아 «확정 안 하고 나갔다» 를 서버에 알리고,
  //   서버가 그 자리에서 지운다. 신호가 못 갈 때(브라우저가 죽는다)의 정본은
  //   시간 청소지만, 대부분은 여기서 바로 치워진다.
  //   ★ 지우기 **전에** 던진다 — 지운 뒤면 리스너가 이미 떨어져 나간다.
  frame.overlay.dispatchEvent(new CustomEvent('nx:modal-closing', {
    detail: result ?? null, bubbles: true,
  }));

  window.setTimeout(() => {
    frame.overlay.remove();
    frame.backdrop.remove();
  }, 200); // .ti-modal 트랜지션 150ms + 여유

  context = stack.length ? (stack[stack.length - 1].opts.context ?? {}) : {};
  (frame.prevFocus as HTMLElement | null)?.focus?.();

  if (result !== null && result !== undefined) frame.opts.onSave?.(result);
  frame.resolve(result ?? null);
}

// ---------------------------------------------------------------- init

export function initModal(): void {
  // 뒤로가기 → 가장 위 팝업만 닫는다(결과 없음).
  window.addEventListener('popstate', () => {
    // 우리가 부른 back 이면 이미 그 팝업은 닫혔다 — 한 번 흘려보낸다.
    if (consumeSelfBack()) return;
    const frame = stack[stack.length - 1];
    if (frame) askClose(frame, true);
  });

  // F311(사장님 실기동 2026-08-09, "팝업이 안 뜨고 전체 화면 — 경우가
  // 있다"): app.js 가 아직 로드 중일 때(느린 회선 수 초) 트리거를 누르면
  // 이 리스너가 없어 링크가 **그대로 이동**했다. 셸의 <head> 조기 가드
  // (partials/_modal_early.html)가 그 틈의 클릭을 막아 두고 큐에 쌓는다 —
  // 배선이 선 지금, 준비 표식을 켜고 마지막 클릭을 이어서 연다.
  const w = window as unknown as {
    __nxModalReady?: boolean; __nxModalQueue?: HTMLElement[];
  };
  w.__nxModalReady = true;
  const queued = w.__nxModalQueue || [];
  w.__nxModalQueue = [];
  const late = queued[queued.length - 1];
  if (late && late.isConnected) {
    // 같은 경로로 연다 — 실제 클릭과 한 벌(아래 리스너와 같은 처리).
    window.setTimeout(() => late.click(), 0);
  }

  // 선언형 트리거:  <a data-nx-modal-open="/url" data-nx-modal-size="inset">
  document.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-nx-modal-open]');
    if (!el) return;
    e.preventDefault();
    const url = el.dataset.nxModalOpen || el.getAttribute('href') || '';
    if (!url) return;
    let ctx: Record<string, unknown> = {};
    if (el.dataset.nxModalContext) {
      try {
        ctx = JSON.parse(el.dataset.nxModalContext);
      } catch {
        /* 잘못된 JSON 이면 빈 값으로 둔다 */
      }
    }
    void open(url, {
      size: (el.dataset.nxModalSize as ModalOptions['size']) || 'sheet',
      title: el.dataset.nxModalTitle || undefined,
      context: ctx,
    }).then((r) => {
      if (r === null) return;
      // 결과를 어디에 반영할지 선언으로 지정할 수 있다.
      //   data-nx-modal-check="#agree"  → 체크박스를 켠다
      const check = el.dataset.nxModalCheck;
      if (check) {
        const box = document.querySelector<HTMLInputElement>(check);
        if (box) {
          box.checked = true;
          box.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      // 부모 페이지가 받아 쓸 수 있는 이벤트도 함께 던진다.
      el.dispatchEvent(
        new CustomEvent('nx:modal:save', { detail: r, bubbles: true }),
      );
    });
  });
}
